import { format } from 'date-fns';
import {
  AlertCircle,
  Calendar as CalendarIcon,
  Check,
  CheckCircle,
  ChevronsUpDown,
  Clock,
  HandCoins,
  Target,
  TrendingUp,
} from 'lucide-react';
import { useEffect, useMemo, useState } from 'react';
import type { DateRange } from 'react-day-picker';
import { useNavigate } from 'react-router-dom';
import {
  CartesianGrid,
  Cell,
  Line,
  LineChart,
  Pie,
  PieChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';
import { DataModeBadge } from '@/components/DataModeBadge';
import { DashboardFilter, type DashboardFilterOption } from '@/components/dashboard/dashboard-filter';
import { HelpSupportButton } from '@/components/help-support';
import { NavigationMetricCardAlt } from '@/components/NavigationMetricCardAlt';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Calendar } from '@/components/ui/calendar';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Command, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList } from '@/components/ui/command';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { useCompany } from '@/contexts/CompanyContext';
import { useCollectionDashboardKpis } from '@/hooks/useCollectionDashboardKpis';
import { useCurrenciesList } from '@/hooks/useCurrenciesData';
import { usePromiseBreakdownGraphQL } from '@/hooks/usePromiseBreakdownGraphQL';
import { usePromisesToPayTableGraphQL } from '@/hooks/usePromisesToPayTableGraphQL';
import { useTeamOverviewData } from '@/hooks/useTeamOverviewData';
import { useWeeklyCollectionTrendGraphQL } from '@/hooks/useWeeklyCollectionTrendGraphQL';
import { cn, formatDate, formatWeekEndingLabel, formatWeekRangeLabel, getCurrencySymbol } from '@/lib/utils';
import { completeWeekTrendPct } from '@/lib/weekly-trend';

// Custom Tooltip Component - currency symbol is injected via a factory function
function createCustomTooltip(currencySymbol: string) {
  // biome-ignore lint/suspicious/noExplicitAny: pre-existing
  const CustomTooltipInner = ({ active, payload }: any) => {
    if (active && payload && payload.length) {
      return (
        <div className="bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg shadow-lg p-3">
          <p className="text-sm font-semibold text-gray-900 dark:text-gray-100">
            Week {formatWeekRangeLabel(payload[0].payload.weekStartDate)}
          </p>
          <p className="text-sm text-gray-600 dark:text-gray-400">
            {currencySymbol}
            {payload[0].value.toLocaleString()} Collected
          </p>
        </div>
      );
    }
    return null;
  };
  return CustomTooltipInner;
}

// Pie chart tooltip content — extracted to satisfy S6478 (no component definitions inside parent)
// biome-ignore lint/suspicious/noExplicitAny: pre-existing
const PieTooltipContent = ({ active, payload }: any) => {
  if (!active || !payload?.length) return null;
  const seg = payload[0].payload;
  const isRed = seg.name === 'Broken';
  const textColor = getTextColorClass(seg.name);
  return (
    <div className="rounded-lg border border-gray-200 bg-white px-4 py-3 shadow-md text-sm min-w-[170px]">
      <div className="flex items-center gap-2 mb-2">
        <span className="inline-block h-2.5 w-2.5 rounded-full flex-shrink-0" style={{ backgroundColor: seg.color }} />
        <span className={`font-semibold ${isRed ? 'text-red-600' : 'text-foreground'}`}>{seg.name}</span>
      </div>
      <div className="flex justify-between gap-6">
        <span className="text-muted-foreground">Invoices</span>
        <span className={`font-medium ${textColor}`}>{seg.count}</span>
      </div>
      {seg.total && (
        <div className="flex justify-between gap-6">
          <span className="text-muted-foreground">Total</span>
          <span className={`font-medium ${textColor}`}>{seg.total}</span>
        </div>
      )}
    </div>
  );
};

function getTextColorClass(name: string): string {
  if (name === 'Broken') return 'text-red-600';
  if (name === 'Closed') return 'text-teal-600';
  return 'text-foreground';
}

function getStatusBadgeClass(status: string): string {
  if (status === 'Broken') return 'bg-red-100 text-red-700 hover:bg-red-100';
  if (status === 'Due today') return 'bg-yellow-100 text-yellow-700 hover:bg-yellow-100';
  return 'bg-green-100 text-green-700 hover:bg-green-100';
}

function formatDaysOverdue(days: number | null): string {
  if (days === null || days <= 0) return '\u2014';
  if (days === 1) return '1 day';
  return `${days} days`;
}

const DEFAULT_FILTER: DashboardFilterOption = { id: 'everyone', name: 'Everyone', type: 'all' };

export default function CollectionsDashboard() {
  const navigate = useNavigate();
  const { selectedCompany } = useCompany();
  const { data: currencies } = useCurrenciesList();
  const [selectedCurrency, setSelectedCurrency] = useState<string>(selectedCompany?.currency || 'GBP');

  useEffect(() => {
    if (selectedCompany?.currency) {
      setSelectedCurrency(selectedCompany.currency);
    }
  }, [selectedCompany?.currency]);

  const currencySymbol = getCurrencySymbol(selectedCurrency);
  const CustomTooltip = useMemo(() => createCustomTooltip(currencySymbol), [currencySymbol]);
  const [selectedFilter, setSelectedFilter] = useState<DashboardFilterOption>(() => {
    const saved = localStorage.getItem('dashboardFilter');
    if (saved) {
      try {
        return JSON.parse(saved);
      } catch {
        /* ignore */
      }
    }
    return DEFAULT_FILTER;
  });
  const [dateRange, setDateRange] = useState<DateRange | undefined>({
    from: new Date(new Date().setDate(new Date().getDate() - 90)),
    to: new Date(),
  });

  const selectedUserId = selectedFilter.id;

  // --- Data hooks ---
  const { data: kpis } = useCollectionDashboardKpis(selectedUserId, selectedCurrency);
  const { data: trendData } = useWeeklyCollectionTrendGraphQL(
    selectedUserId,
    dateRange?.from,
    dateRange?.to,
    selectedCurrency,
  );
  const { data: promiseBreakdown } = usePromiseBreakdownGraphQL(
    selectedUserId,
    dateRange?.from,
    dateRange?.to,
    selectedCurrency,
  );
  const { data: promisesTableGroups } = usePromisesToPayTableGraphQL(
    selectedUserId,
    dateRange?.from,
    dateRange?.to,
    selectedCurrency,
  );
  const { data: teamMembers } = useTeamOverviewData(dateRange?.from, dateRange?.to, selectedCurrency);

  // Flatten promise table groups for display
  const promiseTableRows = useMemo(() => {
    if (!promisesTableGroups) return [];
    return promisesTableGroups.flatMap((group) => group.rows.map((row) => ({ ...row, groupStatus: group.status })));
  }, [promisesTableGroups]);

  // Format promise breakdown for pie chart with currency
  const pieData = useMemo(() => {
    if (!promiseBreakdown) return [];
    return promiseBreakdown.map((seg) => ({
      ...seg,
      total: `${currencySymbol}${seg.totalAmount.toLocaleString()}`,
    }));
  }, [promiseBreakdown, currencySymbol]);

  // Aggregate trend data by weekStartDate so the chart has exactly one point
  // per week even when the backend returns per-controller rows.
  const chartPoints = useMemo(() => {
    const pts = trendData?.points;
    if (!pts || pts.length === 0) return [];
    const byWeek = new Map<string, { collected: number; transactionCount: number }>();
    for (const p of pts) {
      const existing = byWeek.get(p.weekStartDate);
      if (existing) {
        existing.collected += p.collected;
        existing.transactionCount += p.transactionCount;
      } else {
        byWeek.set(p.weekStartDate, { collected: p.collected, transactionCount: p.transactionCount });
      }
    }
    return Array.from(byWeek.entries())
      .sort(([a], [b]) => a.localeCompare(b))
      .map(([weekStartDate, data]) => ({
        week: formatWeekRangeLabel(weekStartDate),
        weekStartDate,
        collected: data.collected,
        transactionCount: data.transactionCount,
      }));
  }, [trendData?.points]);

  // Compute week-on-week trend from the last two complete weeks
  const weeklyTrendPct = useMemo(() => completeWeekTrendPct(chartPoints), [chartPoints]);

  // Dynamically space X-axis ticks so labels never overlap
  const xAxisTickInterval = useMemo(() => {
    const count = chartPoints.length;
    if (count <= 8) return 0;
    if (count <= 16) return 1;
    if (count <= 32) return 3;
    return Math.ceil(count / 8) - 1;
  }, [chartPoints]);

  // Team totals
  const teamTotals = useMemo(() => {
    if (!teamMembers || teamMembers.length === 0) {
      return { activeCases: 0, totalCollected: 0, totalOutstanding: 0, closedThisPeriod: 0 };
    }
    return {
      activeCases: teamMembers.reduce((s, m) => s + m.activeCases, 0),
      totalCollected: teamMembers.reduce((s, m) => s + m.collected, 0),
      totalOutstanding: teamMembers.reduce((s, m) => s + m.outstanding, 0),
      closedThisPeriod: teamMembers.reduce((s, m) => s + m.closed, 0),
    };
  }, [teamMembers]);

  return (
    <div className="p-6 space-y-6">
      {/* Header with Team Member Filter */}
      <div className="flex items-start justify-between">
        <div className="flex items-center gap-3">
          <h1 className="text-3xl font-bold tracking-tight">Collections Dashboard</h1>
          <DataModeBadge />
          <p className="text-muted-foreground">Deep team analytics and trend analysis</p>
        </div>

        {/* Filters */}
        <div className="flex items-center gap-4">
          {/* Currency Filter */}
          <Popover>
            <PopoverTrigger asChild>
              <div
                role="button"
                className={cn(
                  'border-input flex items-center justify-between h-10 w-[320px] px-3 text-sm',
                  'border rounded-md bg-background cursor-pointer',
                  'hover:bg-accent hover:text-accent-foreground',
                )}
              >
                <span className={selectedCurrency ? undefined : 'text-muted-foreground'}>
                  {selectedCurrency
                    ? `${selectedCurrency} - ${(currencies ?? []).find((c) => c.code === selectedCurrency)?.name ?? selectedCurrency}`
                    : 'Select currency...'}
                </span>
                <ChevronsUpDown className="h-4 w-4 shrink-0 opacity-50" />
              </div>
            </PopoverTrigger>
            <PopoverContent className="w-[320px] p-0" align="start">
              <Command>
                <CommandInput placeholder="Search currencies..." />
                <CommandList className="max-h-[200px]">
                  <CommandEmpty>No currencies found.</CommandEmpty>
                  <CommandGroup>
                    {(currencies ?? []).map((c) => (
                      <CommandItem
                        key={c.code}
                        value={`${c.code} - ${c.name}`}
                        onSelect={() => setSelectedCurrency(c.code)}
                      >
                        <Check
                          className={cn('mr-2 h-4 w-4', selectedCurrency === c.code ? 'opacity-100' : 'opacity-0')}
                        />
                        {c.code} - {c.name}
                      </CommandItem>
                    ))}
                  </CommandGroup>
                </CommandList>
              </Command>
            </PopoverContent>
          </Popover>

          {/* Date Range Filter */}
          <Popover>
            <PopoverTrigger asChild>
              <Button variant="outline" className="w-[280px] justify-start text-left font-normal">
                <CalendarIcon className="mr-2 h-4 w-4" />
                {dateRange?.from ? (
                  dateRange.to ? (
                    <>
                      {formatDate(dateRange.from)} - {formatDate(dateRange.to)}
                    </>
                  ) : (
                    formatDate(dateRange.from)
                  )
                ) : (
                  <span>Pick a date range</span>
                )}
              </Button>
            </PopoverTrigger>
            <PopoverContent className="w-auto p-0" align="end">
              <Calendar
                initialFocus
                mode="range"
                defaultMonth={dateRange?.from}
                selected={dateRange}
                onSelect={setDateRange}
                numberOfMonths={2}
              />
            </PopoverContent>
          </Popover>

          {/* User Filter */}
          <DashboardFilter selectedFilter={selectedFilter} onFilterChange={setSelectedFilter} />
          <HelpSupportButton screenId="collections-dashboard" />
        </div>
      </div>

      {/* Dashboard Content */}
      <div className="space-y-4">
        {/* Cases by Status Cards */}
        <div className="grid gap-4 md:grid-cols-3 lg:grid-cols-5">
          <NavigationMetricCardAlt
            title="Active"
            value={(kpis?.activeCount ?? 0).toString()}
            secondaryValue={`${currencySymbol}${(kpis?.activeTotalValue ?? 0).toLocaleString()} total value`}
            icon={Target}
            variant="info"
            onClick={() => {
              const params = new URLSearchParams();
              params.set('status', 'Active');
              params.set('currencies', selectedCurrency);
              if (selectedFilter.id !== 'everyone') {
                params.set('assignedUser', selectedFilter.name);
              }
              navigate(`/collections/queue?${params.toString()}`);
            }}
          />
          <NavigationMetricCardAlt
            title="Promised"
            value={(kpis?.promisedCount ?? 0).toString()}
            secondaryValue={`${currencySymbol}${(kpis?.promisedTotalValue ?? 0).toLocaleString()} committed`}
            icon={HandCoins}
            variant="info"
            onClick={() => {
              const params = new URLSearchParams();
              params.set('status', 'Promised');
              params.set('currencies', selectedCurrency);
              if (selectedFilter.id !== 'everyone') {
                params.set('assignedUser', selectedFilter.name);
              }
              navigate(`/collections/queue?${params.toString()}`);
            }}
          />
          <NavigationMetricCardAlt
            title="Escalated"
            value={(kpis?.escalatedCount ?? 0).toString()}
            secondaryValue={`${currencySymbol}${(kpis?.escalatedTotalValue ?? 0).toLocaleString()} at risk`}
            icon={AlertCircle}
            variant="info"
            onClick={() => {
              const params = new URLSearchParams();
              params.set('status', 'Escalated');
              params.set('currencies', selectedCurrency);
              if (selectedFilter.id !== 'everyone') {
                params.set('assignedUser', selectedFilter.name);
              }
              navigate(`/collections/queue?${params.toString()}`);
            }}
          />
          <NavigationMetricCardAlt
            title="On Hold"
            value={(kpis?.onHoldCount ?? 0).toString()}
            secondaryValue={`${currencySymbol}${(kpis?.onHoldTotalValue ?? 0).toLocaleString()} paused`}
            icon={Clock}
            variant="info"
            onClick={() => {
              const params = new URLSearchParams();
              params.set('status', 'On Hold');
              params.set('currencies', selectedCurrency);
              if (selectedFilter.id !== 'everyone') {
                params.set('assignedUser', selectedFilter.name);
              }
              navigate(`/collections/queue?${params.toString()}`);
            }}
          />
          <NavigationMetricCardAlt
            title="Closed this month"
            value={(kpis?.closedThisMonthCount ?? 0).toString()}
            secondaryValue={`${currencySymbol}${(kpis?.closedThisMonthTotalValue ?? 0).toLocaleString()} recovered`}
            icon={CheckCircle}
            variant="info"
            onClick={() => {
              const params = new URLSearchParams();
              params.set('status', 'Closed');
              params.set('currencies', selectedCurrency);
              if (selectedFilter.id !== 'everyone') {
                params.set('assignedUser', selectedFilter.name);
              }
              navigate(`/collections/queue?${params.toString()}`);
            }}
          />
        </div>

        {/* Weekly Collections Trend */}
        <Card>
          <CardHeader>
            <CardTitle style={{ fontSize: '18px', fontWeight: 700, color: '#111827' }}>
              Weekly Collections Trend{' '}
              <span style={{ fontSize: '13px', fontWeight: 400, color: '#6b7280' }}>
                · weeks covering the selected period
              </span>
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-4">
              {/* Summary Metrics */}
              <div className="grid grid-cols-3 gap-4 pb-4 border-b">
                <div>
                  <div className="text-sm text-muted-foreground">Total Collected</div>
                  <div className="text-2xl font-bold">
                    {currencySymbol}
                    {(trendData?.totalCollected ?? 0).toLocaleString()}
                  </div>
                </div>
                <div>
                  <div className="text-sm text-muted-foreground">Average per Week</div>
                  <div className="text-2xl font-bold">
                    {currencySymbol}
                    {(trendData?.averagePerWeek ?? 0).toLocaleString()}
                  </div>
                </div>
                <div>
                  <div className="text-sm text-muted-foreground">Trend · last 2 complete weeks</div>
                  {weeklyTrendPct !== null ? (
                    <div
                      className={`text-2xl font-bold flex items-center gap-1 ${weeklyTrendPct >= 0 ? 'text-green-600' : 'text-red-600'}`}
                    >
                      <TrendingUp className={`h-5 w-5 ${weeklyTrendPct < 0 ? 'rotate-180' : ''}`} />
                      {weeklyTrendPct >= 0 ? '+' : ''}
                      {weeklyTrendPct}%
                    </div>
                  ) : (
                    <div className="text-2xl font-bold text-muted-foreground">—</div>
                  )}
                </div>
              </div>

              {/* Line Chart */}
              <div className="h-[300px]">
                {chartPoints.length === 0 ? (
                  <div className="flex items-center justify-center h-full">
                    <p className="text-muted-foreground text-sm">No Data</p>
                  </div>
                ) : (
                  <ResponsiveContainer width="100%" height="100%">
                    <LineChart data={chartPoints} margin={{ left: 10, right: 10 }}>
                      <CartesianGrid strokeDasharray="3 3" className="stroke-muted" />
                      <XAxis
                        dataKey="weekStartDate"
                        className="text-xs"
                        tick={{ fill: 'hsl(var(--muted-foreground))' }}
                        tickFormatter={(value) => formatWeekEndingLabel(value)}
                        interval={xAxisTickInterval}
                      />
                      <YAxis
                        className="text-xs"
                        tick={{ fill: 'hsl(var(--muted-foreground))' }}
                        tickFormatter={(value) => `${(value / 1000).toFixed(0)}K`}
                      />
                      <Tooltip content={<CustomTooltip />} />
                      <Line
                        type="monotone"
                        dataKey="collected"
                        stroke="#0d9488"
                        strokeWidth={3}
                        dot={{ fill: '#0d9488', r: 4 }}
                        activeDot={{ r: 6 }}
                        connectNulls={true}
                        isAnimationActive={false}
                      />
                    </LineChart>
                  </ResponsiveContainer>
                )}
              </div>
            </div>
          </CardContent>
        </Card>

        {/* ── Section 2: Promise Overview ── */}
        <div className="border-t border-gray-200 mt-2 pt-6">
          <p className="text-xl font-bold text-gray-800 mb-6">Promise Overview</p>
        </div>

        {/* Promise Reliability + Promises to Pay */}
        <div className="flex gap-6 items-stretch h-[480px]">
          {/* Donut Chart */}
          <Card className="w-1/3 flex-shrink-0 flex flex-col">
            <CardHeader className="pb-2">
              <CardTitle style={{ fontSize: '18px', fontWeight: 700, color: '#111827' }}>
                Promise Breakdown{' '}
                <span style={{ fontSize: '13px', fontWeight: 400, color: '#6b7280' }}>· within selected period</span>
              </CardTitle>
            </CardHeader>
            <CardContent className="flex flex-col items-center justify-center gap-4 p-8 flex-1">
              {pieData.length === 0 ? (
                <div className="flex flex-1 items-center justify-center">
                  <p className="text-muted-foreground text-sm">No promises</p>
                </div>
              ) : (
                <>
                  <div className="flex justify-center">
                    <PieChart width={200} height={200}>
                      <Pie
                        data={pieData}
                        cx={95}
                        cy={95}
                        innerRadius={60}
                        outerRadius={90}
                        dataKey="value"
                        strokeWidth={2}
                      >
                        {pieData.map((entry) => (
                          <Cell key={entry.name} fill={entry.fill} />
                        ))}
                      </Pie>
                      <Tooltip content={<PieTooltipContent />} />
                    </PieChart>
                  </div>
                  <div className="flex flex-wrap justify-center gap-x-6 gap-y-2 text-sm pt-2">
                    {pieData.map(({ name, value, color }) => (
                      <div key={name} className="flex items-center gap-2">
                        <span
                          className="inline-block h-3 w-3 rounded-full flex-shrink-0"
                          style={{ backgroundColor: color }}
                        />
                        <span className="font-semibold text-foreground">{name}</span>
                        <span className="text-muted-foreground">{value}%</span>
                      </div>
                    ))}
                  </div>
                </>
              )}
            </CardContent>
          </Card>

          {/* Promises to Pay */}
          <div className="flex-1 flex flex-col">
            <Card className="flex flex-col h-full">
              <CardHeader className="border-b border-gray-200">
                <div className="flex items-center justify-between">
                  <div>
                    <CardTitle style={{ fontSize: '18px', fontWeight: 700, color: '#111827' }}>
                      Promises to Pay
                    </CardTitle>
                    <CardDescription>Active and broken promises within selected period</CardDescription>
                  </div>
                  <button
                    type="button"
                    className="text-sm font-medium text-teal-600 hover:underline whitespace-nowrap"
                    onClick={() => {
                      const params = new URLSearchParams();
                      params.set('statuses', 'Active,Broken');
                      params.set('currencies', selectedCurrency);
                      if (selectedFilter.id !== 'everyone') {
                        params.set('assignedUser', String(selectedFilter.id));
                      }
                      if (dateRange?.from) {
                        params.set('createdAtFrom', format(dateRange.from, 'yyyy-MM-dd'));
                      }
                      if (dateRange?.to) {
                        params.set('createdAtTo', format(dateRange.to, 'yyyy-MM-dd'));
                      }
                      navigate(`/collections/promises?${params.toString()}`);
                    }}
                  >
                    View all →
                  </button>
                </div>
              </CardHeader>
              <CardContent className="p-0 flex-1 overflow-hidden">
                {promiseTableRows.length === 0 ? (
                  <div className="flex items-center justify-center h-full">
                    <p className="text-muted-foreground text-sm">No promises</p>
                  </div>
                ) : (
                  <div className="overflow-y-auto h-full">
                    <Table className="[&_tr]:divide-x [&_tr]:divide-gray-100">
                      <TableHeader className="sticky top-0 z-10 bg-white shadow-sm">
                        <TableRow className="bg-white hover:bg-white">
                          <TableHead className="font-bold text-gray-700">Customer</TableHead>
                          <TableHead className="font-bold text-gray-700">Credit Controller</TableHead>
                          <TableHead className="font-bold text-gray-700">Status</TableHead>
                          <TableHead className="font-bold text-gray-700">Days Overdue</TableHead>
                          <TableHead className="font-bold text-gray-700 text-right">Amount</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {promiseTableRows.map((row, i) => (
                          <TableRow key={row.promiseId} className={i % 2 === 0 ? 'bg-white' : 'bg-gray-50'}>
                            <TableCell>
                              <div className="font-semibold text-foreground">{row.customerName}</div>
                              <div className="text-xs text-muted-foreground">{row.customerAccountNumber}</div>
                            </TableCell>
                            <TableCell className="text-muted-foreground">{row.assignedUserName}</TableCell>
                            <TableCell>
                              <Badge className={getStatusBadgeClass(row.statusName)}>{row.statusName}</Badge>
                            </TableCell>
                            <TableCell
                              className={
                                row.daysOverdue !== null && row.daysOverdue > 0
                                  ? 'text-red-600 font-medium'
                                  : 'text-muted-foreground'
                              }
                            >
                              {formatDaysOverdue(row.daysOverdue)}
                            </TableCell>
                            <TableCell className="text-right font-bold text-foreground">
                              {currencySymbol}
                              {row.promisedAmount.toLocaleString()}
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </div>
                )}
              </CardContent>
            </Card>
          </div>
        </div>
        {/* end flex row */}

        {/* ── Section 3: Credit Controllers ── */}
        <div className="border-t border-gray-200 pt-8 mt-2" />

        <div>
          <div className="flex items-baseline justify-between mb-4">
            <p className="text-xl font-bold text-gray-800">Team Overview</p>
          </div>
          <div className="space-y-3">
            {(teamMembers ?? []).map((member) => (
              <Card key={member.userId} className="border border-gray-200">
                <CardContent className="flex items-center gap-0 p-0">
                  {/* Identity */}
                  <div className="flex w-72 shrink-0 items-center gap-4 px-6 py-5">
                    <div>
                      <p className="font-semibold text-gray-900">{member.name}</p>
                    </div>
                  </div>
                  {/* Divider */}
                  <div className="h-20 w-px shrink-0 bg-gray-100" />
                  {/* Metrics */}
                  <div className="flex flex-1 items-center justify-around px-6 py-5">
                    <div>
                      <p className="text-xs text-muted-foreground">Active cases</p>
                      <p className="text-lg font-bold text-gray-900">{member.activeCases}</p>
                    </div>
                    <div>
                      <p className="text-xs text-muted-foreground">Collected</p>
                      <p className="text-lg font-bold text-gray-900">
                        {currencySymbol}
                        {member.collected.toLocaleString()}
                      </p>
                    </div>
                    <div>
                      <p className="text-xs text-muted-foreground">Outstanding</p>
                      <p className="text-lg font-bold text-gray-900">
                        {currencySymbol}
                        {member.outstanding.toLocaleString()}
                      </p>
                    </div>
                    <div>
                      <p className="text-xs text-muted-foreground">Closed</p>
                      <p className="text-lg font-bold text-gray-900">{member.closed}</p>
                    </div>
                  </div>
                  {/* View cases */}
                  <div className="shrink-0 px-6">
                    <button
                      className="text-sm font-medium text-emerald-600 hover:text-emerald-700 whitespace-nowrap"
                      onClick={() =>
                        navigate(
                          `/collections/queue?assignedUser=${encodeURIComponent(member.name)}&status=Active&currencies=${encodeURIComponent(selectedCurrency)}`,
                        )
                      }
                    >
                      View cases →
                    </button>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>

          {/* Team Totals Bar */}
          <Card className="mt-4">
            <CardContent className="py-5">
              <div className="flex divide-x divide-gray-100">
                <div className="flex-1 px-6 first:pl-0">
                  <div className="text-sm text-muted-foreground">Active cases</div>
                  <div className="text-2xl font-bold text-gray-900">{teamTotals.activeCases}</div>
                </div>
                <div className="flex-1 px-6">
                  <div className="text-sm text-muted-foreground">Total collected</div>
                  <div className="text-2xl font-bold text-gray-900">
                    {currencySymbol}
                    {teamTotals.totalCollected.toLocaleString()}
                  </div>
                </div>
                <div className="flex-1 px-6">
                  <div className="text-sm text-muted-foreground">Total outstanding</div>
                  <div className="text-2xl font-bold text-gray-900">
                    {currencySymbol}
                    {teamTotals.totalOutstanding.toLocaleString()}
                  </div>
                </div>
                <div className="flex-1 px-6">
                  <div className="text-sm text-muted-foreground">Closed this period</div>
                  <div className="text-2xl font-bold text-green-700">{teamTotals.closedThisPeriod}</div>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  );
}
